/*
  pn-atc — keeps the price inside the add-to-cart button correct, and drives the sticky bar (P7).

  Dawn re-renders parts of the PDP on every variant change and publishes the fetched document on
  PUB_SUB_EVENTS.variantChange. Reading the new price out of THAT document rather than formatting
  `variant.price` here means the currency, the decimal separator and the merchant's money format all
  stay Shopify's job — this file never does money formatting.
*/
(function () {
  const priceIds = (sectionId) => [`PnAtcPrice-${sectionId}`, `PnStickyPrice-${sectionId}`];

  function syncFrom(html, sectionId) {
    priceIds(sectionId).forEach((id) => {
      const source = html.getElementById(id);
      const destination = document.getElementById(id);
      if (source && destination) destination.textContent = source.textContent;
    });

    const variantSource = html.getElementById(`PnStickyVariant-${sectionId}`);
    const variantDest = document.getElementById(`PnStickyVariant-${sectionId}`);
    if (variantSource && variantDest) variantDest.textContent = variantSource.textContent;

    // Mirror the real button's disabled state onto the sticky one so the bar can never offer an
    // add-to-cart the main form would reject.
    const realButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const stickyButton = document.getElementById(`PnStickyButton-${sectionId}`);
    if (realButton && stickyButton) {
      const disabled = realButton.hasAttribute('disabled');
      stickyButton.toggleAttribute('disabled', disabled);
      const label = stickyButton.querySelector('.pn-sticky-atc__label');
      const realLabel = realButton.querySelector('span');
      if (label && realLabel) label.textContent = realLabel.textContent.trim();
    }
  }

  if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
    window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId, html } = event.data || {};
      if (sectionId && html) syncFrom(html, sectionId);
    });
  }

  class PnStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      // Anchor to the sentinel, never to the submit button. <product-info> re-renders the button
      // on init, which left the observer watching a detached node — the bar worked on desktop and
      // silently never appeared on mobile. The sentinel is ours and nothing replaces it.
      this.anchor =
        document.getElementById(`PnAtcSentinel-${this.sectionId}`) ||
        document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
        document.querySelector('.product-form__buttons');
      if (!this.anchor) return;

      /*
        Deliberately a rAF-throttled scroll listener rather than an IntersectionObserver.

        IO fires only on threshold CROSSINGS. A jump that skips the intersecting state entirely —
        window.scrollTo, an anchor link, a restored scroll position on back-navigation — produces no
        callback at all, so the bar silently never appears. Measured with IO: it worked at 768,
        where the sentinel happened to start inside the viewport, and failed at 1440 and 375 where
        it did not. Reading the rect on every frame cannot miss a state it never observed.

        The 20% margin means the bar arrives slightly before the buy box is fully gone, so there is
        never a moment with no add-to-cart on screen.
      */
      this.evaluate = () => {
        const rect = this.anchor.getBoundingClientRect();
        this.setVisible(rect.bottom < window.innerHeight * 0.2);
      };
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.evaluate();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.evaluate();

      // Never sit on top of a field someone is filling in — the engraving inputs and the newsletter
      // field are both in the lower half of the page on mobile.
      this.onFocusIn = (e) => {
        if (e.target.closest('form') && e.target.matches('input, textarea, select')) this.suspend(true);
      };
      this.onFocusOut = () => this.suspend(false);
      document.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('focusout', this.onFocusOut);

      /*
        Suspended for the cart drawer only.

        The consent banner used to suspend it too, on the reasoning that two stacked bottom bars is
        bad design. Measured consequence: a shopper who scrolled past the buy box on a phone had NO
        add-to-cart anywhere while the banner was open, because the buy-box ATC is well below the
        fold on a 664px visible viewport. That is worse than stacking. The bar now sits ABOVE the
        banner instead (--pn-consent-h, published by pn-consent.js), so consent stays uncoverable —
        it is a legal control at z-index 2000000 and must remain so — and the shopper keeps a way to
        buy.
      */
      this.chromeCheck = () => {
        const drawer = document.querySelector('cart-drawer');
        this.suspend(Boolean(drawer && drawer.classList.contains('active')));
      };
      document.addEventListener('click', this.chromeCheck, true);
      // Poll briefly after load: the consent banner is injected by Shopify's own script, so it can
      // arrive after this element has already decided it is clear to show.
      this.chromeTimer = setInterval(this.chromeCheck, 500);
      setTimeout(() => clearInterval(this.chromeTimer), 8000);
      this.chromeCheck();

      /*
        Publish the bar's own height so the page can reserve room for it and the footer's last band
        is never sat on. Mirrors --pn-consent-h (pn-consent.js).

        Measured on the RETRACTED bar, and published whether or not it is currently showing. Tying
        the reserve to visibility looks tempting and is wrong twice over: the bar hides on focusin
        for the newsletter field, which sits IN the footer, so the page would jump the moment a
        shopper clicked it; and it hides while the cart drawer is open, jumping again on close. A
        stable reserve costs one bar's height of scroll at the very bottom and never moves.

        offsetHeight, not getBoundingClientRect().height: the bar is translateY'd off-screen while
        retracted and rect height is unaffected by transform anyway, but offsetHeight is integral
        and does not accumulate sub-pixel drift across resizes.
      */
      this.publishHeight = () => {
        const h = this.offsetHeight || 0;
        document.documentElement.style.setProperty('--pn-atc-h', `${h}px`);
      };
      this.publishHeight();
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(() => this.publishHeight());
        this.ro.observe(this);
      } else {
        window.addEventListener('resize', this.publishHeight, { passive: true });
      }
    }

    disconnectedCallback() {
      if (this.frame) cancelAnimationFrame(this.frame);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('click', this.chromeCheck, true);
      if (this.ro) this.ro.disconnect();
      else window.removeEventListener('resize', this.publishHeight);
      document.documentElement.style.setProperty('--pn-atc-h', '0px');
      clearInterval(this.chromeTimer);
    }

    setVisible(visible) {
      this.wanted = visible;
      this.apply();
    }

    suspend(suspended) {
      this.suspended = suspended;
      this.apply();
    }

    apply() {
      const show = this.wanted && !this.suspended;
      this.hidden = !show;
      this.classList.toggle('pn-sticky-atc--in', show);
    }
  }

  if (!customElements.get('pn-sticky-atc')) {
    customElements.define('pn-sticky-atc', PnStickyAtc);
  }
})();
